import {
  hqSalesDocumentCreateRequestSchema,
  hqSalesDocumentMutationResponseSchema,
  hqSalesDocumentUpdateRequestSchema,
  hqSalesInteractionCreateRequestSchema,
  hqSalesInteractionMutationResponseSchema,
  hqSalesLeadConvertRequestSchema,
  hqSalesLeadConvertResponseSchema,
  hqSalesLeadCreateRequestSchema,
  hqSalesLeadDetailsResponseSchema,
  hqSalesLeadMutationResponseSchema,
  hqSalesLeadsResponseSchema,
  hqSalesLeadUpdateRequestSchema,
  hqSalesTaskCreateRequestSchema,
  hqSalesTaskMutationResponseSchema,
  hqSalesTaskUpdateRequestSchema,
  type HqSalesDocumentCreateRequest,
  type HqSalesDocumentUpdateRequest,
  type HqSalesInteractionCreateRequest,
  type HqSalesLeadConvertRequest,
  type HqSalesLeadCreateRequest,
  type HqSalesLeadUpdateRequest,
  type HqSalesLeadsQuery,
  type HqSalesTaskCreateRequest,
  type HqSalesTaskUpdateRequest,
} from "@kindtutor/api-contract";
import {
  localizedApiErrorSchema as apiErrorSchema,
  parseApiInput,
  throwApiError,
} from "@kindtutor/auth-client";

import { apiBaseURL, apiFetch } from "../auth-client";

export async function fetchHqSalesLeads(
  input: Partial<HqSalesLeadsQuery> = {},
) {
  const query = new URLSearchParams();
  if (input.q) query.set("q", input.q);
  if (input.status) query.set("status", input.status);
  if (input.source) query.set("source", input.source);
  if (input.sort) query.set("sort", input.sort);
  if (input.direction) query.set("direction", input.direction);
  if (input.cursor) query.set("cursor", input.cursor);
  if (input.limit) query.set("limit", String(input.limit));
  const suffix = query.size ? `?${query.toString()}` : "";
  const response = await apiFetch(`${apiBaseURL}/v1/hq/sales/leads${suffix}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok)
    await throwApiError(response, "Nie udało się wczytać leadów.");
  return hqSalesLeadsResponseSchema.parse(await response.json());
}

export async function fetchAllHqSalesLeads() {
  const leads = [];
  let cursor: string | undefined;
  for (let pageNumber = 0; pageNumber < 100; pageNumber += 1) {
    const page = await fetchHqSalesLeads({ cursor, limit: 100 });
    leads.push(...page.leads);
    if (!page.page.hasMore || !page.page.nextCursor) {
      return {
        ...page,
        leads,
        page: { ...page.page, hasMore: false, nextCursor: null },
      };
    }
    cursor = page.page.nextCursor;
  }
  throw new Error("Lista leadów ma zbyt wiele stron do wczytania.");
}

export async function fetchHqSalesLead(leadId: string) {
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/sales/leads/${encodeURIComponent(leadId)}`,
    { credentials: "include", headers: { Accept: "application/json" } },
  );
  if (!response.ok)
    await throwApiError(response, "Nie udało się wczytać leada.");
  return hqSalesLeadDetailsResponseSchema.parse(await response.json());
}

export async function createHqSalesLead(values: HqSalesLeadCreateRequest) {
  const input = parseApiInput(hqSalesLeadCreateRequestSchema, values);
  const response = await apiFetch(`${apiBaseURL}/v1/hq/sales/leads`, {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  const body: unknown = await response.json();
  if (!response.ok) {
    const error = apiErrorSchema.safeParse(body);
    throw new Error(
      error.success
        ? error.data.error.message
        : "Nie udało się utworzyć leada.",
    );
  }
  return hqSalesLeadMutationResponseSchema.parse(body);
}

export async function updateHqSalesLead(
  leadId: string,
  values: HqSalesLeadUpdateRequest,
) {
  const input = parseApiInput(hqSalesLeadUpdateRequestSchema, values);
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/sales/leads/${encodeURIComponent(leadId)}`,
    {
      method: "PUT",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );
  const body: unknown = await response.json();
  if (!response.ok) {
    const error = apiErrorSchema.safeParse(body);
    throw new Error(
      error.success ? error.data.error.message : "Nie udało się zapisać leada.",
    );
  }
  return hqSalesLeadMutationResponseSchema.parse(body);
}

export async function convertHqSalesLead(
  leadId: string,
  values: HqSalesLeadConvertRequest,
) {
  const input = parseApiInput(hqSalesLeadConvertRequestSchema, values);
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/sales/leads/${encodeURIComponent(leadId)}/convert`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );
  const body: unknown = await response.json();
  if (!response.ok) {
    const error = apiErrorSchema.safeParse(body);
    throw new Error(
      error.success
        ? error.data.error.message
        : "Nie udało się utworzyć szkoły z leada.",
    );
  }
  return hqSalesLeadConvertResponseSchema.parse(body);
}

export async function createHqSalesTask(
  leadId: string,
  values: HqSalesTaskCreateRequest,
) {
  const input = parseApiInput(hqSalesTaskCreateRequestSchema, values);
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/sales/leads/${encodeURIComponent(leadId)}/tasks`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );
  const body: unknown = await response.json();
  if (!response.ok) {
    const error = apiErrorSchema.safeParse(body);
    throw new Error(
      error.success ? error.data.error.message : "Nie udało się dodać zadania.",
    );
  }
  return hqSalesTaskMutationResponseSchema.parse(body);
}

export async function updateHqSalesTask(
  leadId: string,
  taskId: string,
  values: HqSalesTaskUpdateRequest,
) {
  const input = parseApiInput(hqSalesTaskUpdateRequestSchema, values);
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/sales/leads/${encodeURIComponent(leadId)}/tasks/${encodeURIComponent(taskId)}`,
    {
      method: "PUT",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );
  const body: unknown = await response.json();
  if (!response.ok) {
    const error = apiErrorSchema.safeParse(body);
    throw new Error(
      error.success
        ? error.data.error.message
        : "Nie udało się zmienić zadania.",
    );
  }
  return hqSalesTaskMutationResponseSchema.parse(body);
}

export async function createHqSalesInteraction(
  leadId: string,
  values: HqSalesInteractionCreateRequest,
) {
  const input = parseApiInput(hqSalesInteractionCreateRequestSchema, values);
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/sales/leads/${encodeURIComponent(leadId)}/interactions`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );
  const body: unknown = await response.json();
  if (!response.ok) {
    const error = apiErrorSchema.safeParse(body);
    throw new Error(
      error.success
        ? error.data.error.message
        : "Nie udało się zapisać kontaktu.",
    );
  }
  return hqSalesInteractionMutationResponseSchema.parse(body);
}

export async function createHqSalesDocument(
  leadId: string,
  values: HqSalesDocumentCreateRequest,
) {
  const input = parseApiInput(hqSalesDocumentCreateRequestSchema, values);
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/sales/leads/${encodeURIComponent(leadId)}/documents`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );
  const body: unknown = await response.json();
  if (!response.ok) {
    const error = apiErrorSchema.safeParse(body);
    throw new Error(
      error.success
        ? error.data.error.message
        : "Nie udało się dodać dokumentu.",
    );
  }
  return hqSalesDocumentMutationResponseSchema.parse(body);
}

export async function updateHqSalesDocument(
  leadId: string,
  documentId: string,
  values: HqSalesDocumentUpdateRequest,
) {
  const input = parseApiInput(hqSalesDocumentUpdateRequestSchema, values);
  const response = await apiFetch(
    `${apiBaseURL}/v1/hq/sales/leads/${encodeURIComponent(leadId)}/documents/${encodeURIComponent(documentId)}`,
    {
      method: "PUT",
      credentials: "include",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(input),
    },
  );
  const body: unknown = await response.json();
  if (!response.ok) {
    const error = apiErrorSchema.safeParse(body);
    throw new Error(
      error.success
        ? error.data.error.message
        : "Nie udało się zmienić dokumentu.",
    );
  }
  return hqSalesDocumentMutationResponseSchema.parse(body);
}
